import './App.css';
import { useEffect } from 'react';
import Header from './components/Header';
import AppContent from './components/AppContent';
import initScripts from './utils/script';
import Drawer from './components/Drawer';
import Home from './home/Home';
import Categories from './categories/Categories';
import SingleCategory from './categories/SingleCategory';
import SingleApp from './single-app/SingleApp';
import RecommendedAppAndGames from './recommended/RecommendedAppsAndGames';
import TopCharts from './top-charts/TopCharts';
import LatestAppsAndGames from './latest/LatestAppsAndGames';
import SingleCollection from './collection/SingleCollection';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import BlogHome from './blogs/BlogHome';
import GameHome from './games/GameHome';
import SingleBlogPost from './single-blog/SingleBlogPost';
import BlogCategories from './blogs/categories/BlogCategories';
import SingleBlogCategory from './blogs/single-category/SingleBlogCategory';
import FeaturedBlogPosts from './blogs/featured-posts/FeatureBlogPosts';
import LatestBlogPosts from './blogs/latest-posts/LatestBlogPosts';
import PopularBlogPosts from './blogs/popular/PopularBlogPosts';
import AuthorPage from './blogs/author/AuthorPage';
import PostByTags from './blogs/tags/PostByTags';
import { HelmetProvider } from "react-helmet-async";
import Footer from './components/Footer';
import BottomNav from './components/BottomNav';
import Login from './auth/Login';
import Register from './auth/Register';
import ForgotPassword from './auth/ForgotPassword';
import ResetPassword from './auth/ResetPassword';
import MyApps from './my-apps/MyApps';
import Profile from './user/Profile';
import Wishlist from './user/Wishlist';
import { SettingsProvider } from "./context/SettingsProvider";
import { ToastProvider } from "./context/ToastProvider";
import ToastContainer from "./components/Toast";
import DeveloperPage from './developer/DeveloperPage';
import { AuthProvider } from "./providers/AuthProvider";
import Search from './search/Search';
import { TranslationProvider } from "./hooks/useTranslation";
import { LoadingProvider } from "./context/LoadingContext";
import ProgressBar from "./components/ProgressBar";
import SmartAppBanner from "./components/SmartAppBanner";

// Pages
import AboutPage from './pages/AboutPage';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import ContentPolicy from './pages/ContentPolicy';
import FamilyPolicy from './pages/FamilyPolicy';
import DeveloperPolicy from './pages/DeveloperPolicy';
import PageNotFound from './errors/PageNotFound';
import FAQsPage from './pages/FAQsPage';
import ParentalGuide from './pages/ParentalGuide';
import HelpCenterPage from './help-center/HelpCenterPage';
import HelpArticlePage from './help-center/HelpArticlePage';
import HelpCategoryPage from './help-center/HelpCategoryPage';
import HelpSearchPage from './help-center/HelpSearchPage';
import ContactPage from './contact/ContactPage';
import ReportBugPage from './pages/ReportBugPage';
import DevelopersPage from './pages/DevelopersPage';
import PressPage from './pages/PressPage';
import CareersPage from './pages/CareersPage';
import SitemapPage from './pages/SitemapPage';

// App Import Setup
import AppHome from './apps/AppHome';
import AppCategories from './apps/categories/AppCategories';
import RecommendedApps from './apps/recommended/RecommendedApps';
import TopAppChart from './apps/top-chart/TopAppChart';
import LatestApps from './apps/latest/LatestApps';

// Games Import Setup
import GameCategories from './games/categories/GameCategories';
import RecommendedGames from './games/recommended/RecommendedGames';
import TopGameChart from './games/top-chart/TopGameChart';
import LatestGames from './games/latest/LatestGames';

function App() {
    useEffect(() => {
        initScripts();
    }, []);

    return (
        <LoadingProvider>
            <ProgressBar />
            <TranslationProvider>
                <ToastProvider>
                    <SettingsProvider>
                        <AuthProvider>
                            <Router>
                                <AppContent>
                                    <Header />
                                    <main>
                                        <div className="drawer-overlay" id="drawerOverlay"></div>
                                        <Drawer />
                                        <HelmetProvider>
                                            <Routes>
                                                <Route path="/" element={<Home />} />
                                                <Route path='/login' element={<Login />} />
                                                <Route path='/register' element={<Register />} />
                                                <Route path='/forgot-password' element={<ForgotPassword />} />
                                                <Route path='/reset-password' element={<ResetPassword />} />
                                                <Route path='/my-apps' element={<MyApps />} />
                                                <Route path='/profile' element={<Profile />} />
                                                <Route path='/settings' element={<Profile />} />
                                                <Route path='/wishlist' element={<Wishlist />} />
                                                <Route path="/apps" element={<AppHome />} />
                                                <Route path="/recommended" element={<RecommendedAppAndGames />} />
                                                <Route path="/top-charts" element={<TopCharts />} />
                                                <Route path="/latest" element={<LatestAppsAndGames />} />
                                                <Route path="/blogs" element={<BlogHome />} />
                                                <Route path="/blog/:slug" element={<SingleBlogPost />} />
                                                <Route path="/categories" element={<Categories />} />
                                                <Route path="/apps/category/:slug" element={<SingleCategory />} />
                                                <Route path='/blogs/category/:categorySlug' element={<SingleBlogCategory />} />
                                                <Route path="/collection/:slug" element={<SingleCollection />} />
                                                <Route path='/blogs/tag/:slug' element={<PostByTags />} />
                                                <Route path="/app/details/:slug" element={<SingleApp />} />
                                                <Route path="/blogs/author/:authorId" element={<AuthorPage />} />
                                                <Route path="/blogs/categories" element={<BlogCategories />} />
                                                <Route path="/blogs/featured" element={<FeaturedBlogPosts />} />
                                                <Route path="/blogs/latest" element={<LatestBlogPosts />} />
                                                <Route path="/blogs/popular" element={<PopularBlogPosts />} />
                                                <Route path="/games" element={<GameHome />} />
                                                <Route path="/games/categories" element={<GameCategories />} />
                                                <Route path="/games/recommended" element={<RecommendedGames />} />
                                                <Route path="/games/top-charts" element={<TopGameChart />} />
                                                <Route path="/games/latest" element={<LatestGames />} />
                                                <Route path="/developer/:slug" element={<DeveloperPage />} />
                                                <Route path="/apps/categories" element={<AppCategories />} />
                                                <Route path="/apps/recommended" element={<RecommendedApps />} />
                                                <Route path="/apps/top-charts" element={<TopAppChart />} />
                                                <Route path="/apps/latest" element={<LatestApps />} />
                                                <Route path="/search" element={<Search />} />
                                                <Route path="*" element={<PageNotFound />} />
                                                {/* Pages Routes */}
                                                <Route path="/about" element={<AboutPage />} />
                                                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                                                <Route path="/terms-of-service" element={<TermsOfService />} />
                                                <Route path="/content-policy" element={<ContentPolicy />} />
                                                <Route path="/family-policy" element={<FamilyPolicy />} />
                                                <Route path="/developer-policy" element={<DeveloperPolicy />} />
                                                <Route path="/faqs" element={<FAQsPage />} />
                                                <Route path="/parental-guide" element={<ParentalGuide />} />
                                                <Route path="/help" element={<HelpCenterPage />} />
                                                <Route path="/help/article/:slug" element={<HelpArticlePage />} />
                                                <Route path="/help/category/:slug" element={<HelpCategoryPage />} />
                                                <Route path="/help/search" element={<HelpSearchPage />} />
                                                <Route path="/contact" element={<ContactPage />} />
                                                <Route path="/report-bug" element={<ReportBugPage />} />
                                                <Route path="/developers" element={<DevelopersPage />} />
                                                <Route path="/press" element={<PressPage />} />
                                                <Route path="/careers" element={<CareersPage />} />
                                                <Route path="/sitemap" element={<SitemapPage />} />
                                            </Routes>
                                        </HelmetProvider>
                                    </main>
                                    <SmartAppBanner />
                                    <Footer />
                                    <BottomNav />
                                </AppContent>
                                <ToastContainer />
                            </Router>
                        </AuthProvider>
                    </SettingsProvider>
                </ToastProvider>
            </TranslationProvider>
        </LoadingProvider>
    );
}


export default App;
